import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronDown, Smartphone, GripVertical } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";

export default function DevicesManager() {
  const [brands, setBrands] = useState([]);
  const [modelsByBrand, setModelsByBrand] = useState({});
  const [expanded, setExpanded] = useState(null);

  const [brandDialog, setBrandDialog] = useState(false);
  const [brandForm, setBrandForm] = useState({ name: "", order: 0, active: true });
  const [editBrand, setEditBrand] = useState(null);

  const [modelDialog, setModelDialog] = useState(false);
  const [modelForm, setModelForm] = useState({ name: "", brand_id: "", order: 0, active: true });
  const [editModel, setEditModel] = useState(null);

  const [del, setDel] = useState(null); // {type, item}
  const [dragItem, setDragItem] = useState(null); // {brandId, index}

  const loadBrands = () => api.get("/brands", { params: { all: true } }).then((r) => setBrands(r.data)).catch(() => {});
  const loadModels = (brandId) => api.get("/phone-models", { params: { brand_id: brandId, all: true } }).then((r) => setModelsByBrand((s) => ({ ...s, [brandId]: r.data }))).catch(() => {});

  useEffect(() => { loadBrands(); }, []);

  const toggleExpand = (b) => {
    const next = expanded === b.id ? null : b.id;
    setExpanded(next);
    if (next && !modelsByBrand[b.id]) loadModels(b.id);
  };

  const saveBrand = async () => {
    if (!brandForm.name.trim()) return toast.error("Nama merek wajib diisi");
    try {
      if (editBrand) await api.put(`/admin/brands/${editBrand.id}`, brandForm);
      else await api.post("/admin/brands", brandForm);
      toast.success("Merek disimpan"); setBrandDialog(false); loadBrands();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const saveModel = async () => {
    if (!modelForm.name.trim()) return toast.error("Nama tipe wajib diisi");
    try {
      if (editModel) await api.put(`/admin/phone-models/${editModel.id}`, modelForm);
      else await api.post("/admin/phone-models", modelForm);
      toast.success("Tipe HP disimpan"); setModelDialog(false); loadModels(modelForm.brand_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const confirmDelete = async () => {
    try {
      if (del.type === "brand") { await api.delete(`/admin/brands/${del.item.id}`); loadBrands(); }
      else { await api.delete(`/admin/phone-models/${del.item.id}`); loadModels(del.item.brand_id); }
      toast.success("Berhasil dihapus");
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
    finally { setDel(null); }
  };

  const handleDrop = async (brandId, dropIndex) => {
    if (!dragItem || dragItem.brandId !== brandId || dragItem.index === dropIndex) { setDragItem(null); return; }
    const list = [...(modelsByBrand[brandId] || [])];
    const [moved] = list.splice(dragItem.index, 1);
    list.splice(dropIndex, 0, moved);
    setModelsByBrand((s) => ({ ...s, [brandId]: list }));
    setDragItem(null);
    try {
      await api.put("/admin/phone-models/reorder", { ids: list.map((m) => m.id) });
      toast.success("Urutan tipe HP diperbarui");
    } catch {
      toast.error("Gagal menyimpan urutan");
      loadModels(brandId);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Merek & Tipe HP</h1>
          <p className="mt-1 text-sm text-muted-foreground">Kelola merek dan tipe/model HP yang kompatibel.</p>
        </div>
        <button data-testid="add-brand-btn" onClick={() => { setEditBrand(null); setBrandForm({ name: "", order: brands.length, active: true }); setBrandDialog(true); }} className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> Merek</button>
      </div>

      <div className="mt-6 space-y-2">
        {brands.map((b) => (
          <div key={b.id} data-testid={`brand-row-${b.id}`} className="rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between px-4 py-3">
              <button onClick={() => toggleExpand(b)} className="flex items-center gap-2 font-medium">
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded === b.id ? "rotate-180" : ""}`} />
                {b.name} {!b.active && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">Nonaktif</span>}
              </button>
              <div className="flex gap-1">
                <button onClick={() => { setModelForm({ name: "", brand_id: b.id, order: (modelsByBrand[b.id]||[]).length, active: true }); setEditModel(null); setModelDialog(true); if (!modelsByBrand[b.id]) loadModels(b.id); }} className="rounded-lg px-2 py-1.5 text-xs font-medium text-accent hover:bg-accent/10" data-testid={`add-model-${b.id}`}>+ Tipe</button>
                <button onClick={() => { setEditBrand(b); setBrandForm({ name: b.name, order: b.order, active: b.active }); setBrandDialog(true); }} className="rounded-lg p-2 hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-brand-${b.id}`} onClick={() => setDel({ type: "brand", item: b })} className="rounded-lg p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            {expanded === b.id && (
              <div className="border-t border-border px-4 py-3 space-y-2">
                {(modelsByBrand[b.id] || []).length === 0 && <p className="text-sm text-muted-foreground">Belum ada tipe HP.</p>}
                {(modelsByBrand[b.id] || []).map((m, mi) => (
                  <div
                    key={m.id}
                    draggable
                    onDragStart={() => setDragItem({ brandId: b.id, index: mi })}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(b.id, mi)}
                    onDragEnd={() => setDragItem(null)}
                    data-testid={`model-row-${m.id}`}
                    className={`flex items-center justify-between rounded-lg bg-secondary px-3 py-2 text-sm transition-all ${dragItem?.brandId === b.id && dragItem?.index === mi ? "opacity-40" : ""}`}
                  >
                    <span className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground/50 active:cursor-grabbing" />
                      <Smartphone className="h-3.5 w-3.5 text-muted-foreground" /> {m.name} {!m.active && <span className="text-xs text-muted-foreground">(nonaktif)</span>}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditModel(m); setModelForm({ name: m.name, brand_id: b.id, order: m.order, active: m.active }); setModelDialog(true); }} className="rounded p-1.5 hover:bg-background"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setDel({ type: "model", item: m })} className="rounded p-1.5 text-destructive hover:bg-background"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Brand dialog */}
      <Dialog open={brandDialog} onOpenChange={setBrandDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editBrand ? "Edit" : "Tambah"} Merek</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Merek</Label><Input data-testid="brand-name-input" value={brandForm.name} onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Urutan</Label><Input type="number" value={brandForm.order} onChange={(e) => setBrandForm({ ...brandForm, order: Number(e.target.value) })} className="mt-1" /></div>
            <div className="flex items-center justify-between"><Label>Aktif</Label><Switch checked={brandForm.active} onCheckedChange={(v) => setBrandForm({ ...brandForm, active: v })} /></div>
          </div>
          <DialogFooter><button data-testid="save-brand-btn" onClick={saveBrand} className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Simpan</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Model dialog */}
      <Dialog open={modelDialog} onOpenChange={setModelDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editModel ? "Edit" : "Tambah"} Tipe HP</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Tipe/Model</Label><Input data-testid="model-name-input" value={modelForm.name} onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })} placeholder="cth: iPhone 17 Pro Max" className="mt-1" /></div>
            <div><Label>Urutan</Label><Input type="number" value={modelForm.order} onChange={(e) => setModelForm({ ...modelForm, order: Number(e.target.value) })} className="mt-1" /></div>
            <div className="flex items-center justify-between"><Label>Aktif</Label><Switch checked={modelForm.active} onCheckedChange={(v) => setModelForm({ ...modelForm, active: v })} /></div>
          </div>
          <DialogFooter><button data-testid="save-model-btn" onClick={saveModel} className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Simpan</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {del?.type === "brand" ? "merek" : "tipe HP"}?</AlertDialogTitle>
            <AlertDialogDescription>"{del?.item?.name}" akan dihapus. {del?.type === "brand" ? "Semua tipe HP di dalamnya juga dihapus. " : ""}Produk tidak akan terhapus, hanya kompatibilitasnya yang dilepas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-device" onClick={confirmDelete} className="bg-destructive">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
